import React, { useState, useEffect, useRef } from 'react';
import { coachingApi } from '../../services/api';
import useVisiblePolling from '../../hooks/useVisiblePolling';
import { 
  Users, 
  Star, 
  MessageSquare, 
  Send, 
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Edit3,
  Play,
  Upload,
  Link,
  Save,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Award,
  Settings,
  Bell,
  ArrowLeft,
  Calendar,
  RefreshCw,
  Video,
  Clock,
  FileText,
  Reply,
  AlertCircle,
  Flame,
  HeartCrack,
  Snowflake,
  Ghost,
  Check
} from 'lucide-react';
import './CoachingPortal.css';

const CoachingPortal = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('students');
  const [students, setStudents] = useState([]);
  const [mentorshipStudents, setMentorshipStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [replyingToTrade, setReplyingToTrade] = useState(null); // Track which trade is being replied to
  const [tradeReplyText, setTradeReplyText] = useState('');
  const [sendingTradeReply, setSendingTradeReply] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState(null); // Track which message is being replied to
  const [alerts, setAlerts] = useState([]);
  const [runningScan, setRunningScan] = useState(false);
  const messagesEndRef = useRef(null);
  const currentStudentIdRef = useRef(null);  // Track which student we're loading to prevent race conditions

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Keep the student list (+ unread counts) fresh so new messages from students
  // show up without manual refresh. Pauses when tab is hidden.
  useVisiblePolling(async () => {
    if (activeTab === 'students') {
      const data = await coachingApi.getStudents();
      setStudents(data.students || []);
    } else if (activeTab === 'mentorship') {
      const data = await coachingApi.getMentorshipStudents();
      setMentorshipStudents(data.students || []);
    } else if (activeTab === 'alerts') {
      const data = await coachingApi.listAlerts();
      setAlerts(data.alerts || []);
    }
  }, { intervalMs: 30000, deps: [activeTab] });

  const loadAlerts = async () => {
    try {
      const data = await coachingApi.listAlerts();
      setAlerts(data.alerts || []);
    } catch (e) { console.warn('loadAlerts failed:', e); }
  };

  const handleResolveAlert = async (alertId) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
    try {
      await coachingApi.resolveAlert(alertId);
    } catch (e) {
      console.error('resolveAlert failed:', e);
      loadAlerts();
    }
  };

  const handleRunAlertScan = async () => {
    setRunningScan(true);
    try {
      await coachingApi.runAlertScan();
      await loadAlerts();
    } catch (e) {
      console.error('run scan failed:', e);
      alert('Failed to run alert scan');
    } finally {
      setRunningScan(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'students') {
        const data = await coachingApi.getStudents();
        setStudents(data.students || []);
      } else if (activeTab === 'mentorship') {
        const data = await coachingApi.getMentorshipStudents();
        setMentorshipStudents(data.students || []);
      } else if (activeTab === 'alerts') {
        const data = await coachingApi.listAlerts();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudentDetail = async (studentId, month = null) => {
    // Store this student ID as the one we're currently loading
    currentStudentIdRef.current = studentId;
    
    try {
      setDetailLoading(true);
      setStudentDetail(null);  // Clear previous data first
      setCalendarData(null);
      setSelectedDay(null);
      setSchedule(null);
      setSessions([]);
      
      // Pass the month parameter to get month-specific performance data
      const data = await coachingApi.getStudentDetail(studentId, month || calendarMonth);
      
      // RACE CONDITION FIX: Check if user clicked on another student while this was loading
      if (currentStudentIdRef.current !== studentId) {
        console.log('Race condition prevented: user selected different student');
        return; // Abort - user has selected a different student
      }
      
      if (!data || !data.student) {
        console.error('Invalid student data received');
        alert('Failed to load student data');
        return;
      }
      
      setStudentDetail(data);
      
      // If sharing enabled, load calendar
      if (data.sharing_enabled) {
        loadStudentCalendar(studentId, month || calendarMonth);
      }
      
      // If mentorship student, load sessions and schedule
      if (data.is_mentorship_student) {
        try {
          const [sessionsData, scheduleData] = await Promise.all([
            coachingApi.getStudentSessions(studentId),
            coachingApi.getStudentSchedule(studentId)
          ]);
          
          // RACE CONDITION FIX: Check again before updating state
          if (currentStudentIdRef.current !== studentId) {
            console.log('Race condition prevented in sessions load');
            return;
          }
          
          setSessions(sessionsData.sessions || []);
          setSchedule(scheduleData.schedule);
        } catch (sessionError) {
          console.error('Failed to load sessions:', sessionError);
        }
      }
    } catch (error) {
      // RACE CONDITION FIX: Only show error if this is still the active request
      if (currentStudentIdRef.current === studentId) {
        console.error('Failed to load student detail:', error);
        // Show the actual error message from the server if available
        const errorMsg = error?.response?.data?.detail || error?.message || 'Unknown error';
        alert(`Failed to load student data: ${errorMsg}`);
        setSelectedStudent(null);
      }
    } finally {
      // RACE CONDITION FIX: Only clear loading if this is still the active request
      if (currentStudentIdRef.current === studentId) {
        setDetailLoading(false);
      }
    }
  };

  const loadStudentCalendar = async (studentId, month) => {
    try {
      setCalendarLoading(true);
      const data = await coachingApi.getStudentCalendar(studentId, month);
      
      // RACE CONDITION FIX: Only update if still viewing the same student
      if (currentStudentIdRef.current === studentId) {
        setCalendarData(data);
      }
    } catch (error) {
      console.error('Failed to load calendar:', error);
      if (currentStudentIdRef.current === studentId) {
        setCalendarData(null);
      }
    } finally {
      if (currentStudentIdRef.current === studentId) {
        setCalendarLoading(false);
      }
    }
  };

  const handleMonthChange = (direction) => {
    const [year, month] = calendarMonth.split('-').map(Number);
    let newMonth, newYear;
    
    if (direction === 'prev') {
      newMonth = month === 1 ? 12 : month - 1;
      newYear = month === 1 ? year - 1 : year;
    } else {
      newMonth = month === 12 ? 1 : month + 1;
      newYear = month === 12 ? year + 1 : year;
    }
    
    const newMonthStr = `${newYear}-${String(newMonth).padStart(2, '0')}`;
    setCalendarMonth(newMonthStr);
    setSelectedDay(null);
    
    if (selectedStudent && studentDetail?.sharing_enabled) {
      loadStudentCalendar(selectedStudent.id, newMonthStr);
      // Also reload performance data for the new month
      loadStudentDetail(selectedStudent.id, newMonthStr);
    }
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    loadStudentDetail(student.id, calendarMonth);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !selectedStudent) return;

    setSending(true);
    try {
      let messageToSend = newMessage.trim();
      
      // If replying to a message, include the quoted context
      if (replyingToMessage) {
        const originalSender = replyingToMessage.sender === 'coach' ? 'You' : selectedStudent.name;
        const truncatedOriginal = replyingToMessage.message.length > 100 
          ? replyingToMessage.message.substring(0, 100) + '...'
          : replyingToMessage.message;
        messageToSend = `↩️ Replying to ${originalSender}:\n> "${truncatedOriginal}"\n\n${newMessage.trim()}`;
      }
      
      const message = await coachingApi.sendMessageToStudent(selectedStudent.id, messageToSend);
      setStudentDetail(prev => ({
        ...prev,
        messages: [...(prev.messages || []), message]
      }));
      setNewMessage('');
      setReplyingToMessage(null); // Clear reply state
      scrollToBottom();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Cancel message reply
  const cancelMessageReply = () => {
    setReplyingToMessage(null);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Handle reply/comment on a trade
  const handleTradeReply = async (trade) => {
    if (!tradeReplyText.trim() || sendingTradeReply || !selectedStudent) return;

    setSendingTradeReply(true);
    try {
      // Format the trade context for the message
      const tradeDate = selectedDay?.date || 'Unknown date';
      const pair = trade.currency_pair || trade.pair || 'Unknown';
      const direction = trade.direction || '';
      const pl = trade.profit_loss || '0';
      const outcome = trade.outcome === 'Win' ? '+' : '-';
      const hasNote = trade.notes && trade.notes.trim();
      
      // Build contextual message header
      const contextHeader = hasNote 
        ? `📝 Re: Your ${pair} ${direction} trade on ${tradeDate} (${outcome}$${pl})`
        : `💬 Coach comment on your ${pair} ${direction} trade on ${tradeDate} (${outcome}$${pl})`;
      
      // Include original note if replying
      const originalNote = hasNote ? `\n\n> "${trade.notes}"` : '';
      
      // Full message with context
      const fullMessage = `${contextHeader}${originalNote}\n\n${tradeReplyText.trim()}`;
      
      // Send via existing messaging system
      const message = await coachingApi.sendMessageToStudent(selectedStudent.id, fullMessage);
      
      // Update messages in state
      setStudentDetail(prev => ({
        ...prev,
        messages: [...(prev.messages || []), message]
      }));
      
      // Clear reply state
      setReplyingToTrade(null);
      setTradeReplyText('');
      
      // Scroll to messages section
      scrollToBottom();
      
      // Show success feedback
      alert('Message sent successfully!');
    } catch (error) {
      console.error('Failed to send trade reply:', error);
      alert('Failed to send message');
    } finally {
      setSendingTradeReply(false);
    }
  };

  // Cancel trade reply
  const cancelTradeReply = () => {
    setReplyingToTrade(null);
    setTradeReplyText('');
  };

  const handleAddToMentorship = async (userId) => {
    try {
      await coachingApi.addMentorshipStudent(userId);
      alert('Student added to mentorship program!');
      loadStudentDetail(userId);
      loadData();
    } catch (error) {
      console.error('Failed to add to mentorship:', error);
      alert('Failed to add student to mentorship');
    }
  };

  const handleRemoveFromMentorship = async (userId) => {
    if (!window.confirm('Remove this student from the mentorship program?')) return;
    try {
      await coachingApi.removeMentorshipStudent(userId);
      loadStudentDetail(userId);
      loadData();
    } catch (error) {
      console.error('Failed to remove from mentorship:', error);
      alert('Failed to remove student');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const loadNotificationConfig = async () => {
    try {
      const config = await coachingApi.getAdminNotificationConfig();
      setNotificationConfig(config);
    } catch (error) {
      console.error('Failed to load notification config:', error);
    }
  };

  const handleSaveNotificationConfig = async () => {
    try {
      await coachingApi.updateAdminNotificationConfig(notificationConfig);
      alert('Settings saved!');
    } catch (error) {
      console.error('Failed to save config:', error);
      alert('Failed to save settings');
    }
  };

  const handleTestNotification = async () => {
    try {
      await coachingApi.testAdminNotification();
      alert('Test notification sent!');
    } catch (error) {
      console.error('Failed to send test:', error);
      alert('Failed to send test notification. Check your Telegram configuration.');
    }
  };

  const renderStudentList = () => {
    const studentList = activeTab === 'students' ? students : mentorshipStudents;
    
    if (loading) {
      return (
        <div className="loading-state">
          <Loader2 className="spin" size={24} />
        </div>
      );
    }

    if (studentList.length === 0) {
      return (
        <div className="empty-state">
          <p>{activeTab === 'students' ? 'No paid students yet' : 'No mentorship students yet'}</p>
        </div>
      );
    }

    return (
      <div className="student-list">
        {studentList.map((student) => (
          <div
            key={student.id}
            className={`student-item ${selectedStudent?.id === student.id ? 'selected' : ''}`}
            onClick={() => handleSelectStudent(student)}
          >
            <div className="student-avatar">
              {student.profile_picture ? (
                <img src={student.profile_picture} alt={student.name} />
              ) : (
                <span>{student.name?.charAt(0) || '?'}</span>
              )}
              {student.sharing_enabled && <span className="sharing-dot" title="Sharing enabled" />}
              {student.is_mentorship_student && <Star className="mentorship-badge" size={12} />}
            </div>
            <div className="student-info">
              <span className="student-name">{student.name}</span>
              <span className="student-meta">
                {student.sharing_enabled ? (
                  <span className="sharing-status">Sharing</span>
                ) : (
                  <span className="not-sharing">Not sharing</span>
                )}
              </span>
            </div>
            {student.unread_count > 0 && (
              <span className="unread-badge">{student.unread_count}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStudentDetail = () => {
    if (!selectedStudent) {
      return (
        <div className="no-selection">
          <Users size={48} />
          <p>Select a student to view details</p>
        </div>
      );
    }

    if (detailLoading) {
      return (
        <div className="loading-state">
          <Loader2 className="spin" size={32} />
          <p>Loading student data...</p>
        </div>
      );
    }

    if (!studentDetail) {
      return (
        <div className="no-selection">
          <p>Failed to load student details</p>
        </div>
      );
    }

    return (
      <div className="student-detail">
        {/* Student Header */}
        <div className="detail-header">
          <div className="student-profile">
            <div className="profile-avatar">
              {studentDetail.student.profile_picture ? (
                <img src={studentDetail.student.profile_picture} alt="" />
              ) : (
                <span>{studentDetail.student.name?.charAt(0) || '?'}</span>
              )}
            </div>
            <div className="profile-info">
              <h3>{studentDetail.student.name}</h3>
              <span className="profile-email">{studentDetail.student.email}</span>
              <span className="profile-membership">{studentDetail.student.membership_name}</span>
            </div>
          </div>
          
          <div className="detail-actions">
            {studentDetail.is_mentorship_student ? (
              <button 
                className="btn-secondary"
                onClick={() => handleRemoveFromMentorship(selectedStudent.id)}
              >
                Remove from Mentorship
              </button>
            ) : (
              <button 
                className="btn-primary"
                onClick={() => handleAddToMentorship(selectedStudent.id)}
              >
                <Plus size={16} />
                Add to Mentorship
              </button>
            )}
          </div>
        </div>

        {/* Performance Stats (if sharing) - Synced with Calendar Month */}
        {studentDetail.sharing_enabled && studentDetail.performance && (
          <div className="performance-section">
            <h4>
              <BarChart3 size={16} />
              {(() => {
                const [year, month] = calendarMonth.split('-').map(Number);
                return new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
              })()} Performance
            </h4>
            {/* Performance Stats - Green for positive, Red for negative */}
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-label">Total P/L</span>
                <span className={`stat-value ${parseFloat(studentDetail.performance.total_pl) >= 0 ? 'positive' : 'negative'}`}>
                  ${studentDetail.performance.total_pl}
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Win Rate</span>
                <span className={`stat-value ${parseFloat(studentDetail.performance.win_rate) >= 50 ? 'positive' : 'negative'}`}>
                  {studentDetail.performance.win_rate}%
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Trades</span>
                <span className={`stat-value ${studentDetail.performance.wins >= studentDetail.performance.losses ? 'positive' : 'negative'}`}>
                  {studentDetail.performance.wins}W / {studentDetail.performance.losses}L
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Habit Score</span>
                <span className={`stat-value ${parseFloat(studentDetail.performance.avg_habit_score) >= 70 ? 'positive' : 'negative'}`}>
                  {studentDetail.performance.avg_habit_score}%
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Trading Accounts (if student has any) */}
        {studentDetail.trading_accounts && studentDetail.trading_accounts.length > 0 && (
          <div className="trading-accounts-section">
            <h4>Trading Accounts</h4>
            <div className="trading-accounts-grid">
              {studentDetail.trading_accounts.map((account, index) => (
                <div key={account.id || index} className={`account-card ${account.type?.toLowerCase()}`}>
                  <div className="account-host">{account.host || 'Unknown'}</div>
                  <div className="account-type-badge">{account.type || 'Challenge'}</div>
                  <div className={`account-balance ${(account.balance || 0) < 0 ? 'negative' : ''}`}>
                    ${(account.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              ))}
            </div>
            <div className="accounts-total">
              <span>Total Balance:</span>
              <span className={`total-amount ${studentDetail.trading_accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0) < 0 ? 'negative' : ''}`}>
                ${studentDetail.trading_accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        )}

        {/* Trading Calendar (if can view data) */}
        {(studentDetail.sharing_enabled || studentDetail.is_mentorship_student) && (
          <StudentCalendar
            calendarData={calendarData}
            calendarMonth={calendarMonth}
            calendarLoading={calendarLoading}
            selectedDay={selectedDay}
            onMonthChange={handleMonthChange}
            onSelectDay={setSelectedDay}
            onRefresh={() => loadStudentCalendar(selectedStudent.id, calendarMonth)}
            replyingToTrade={replyingToTrade}
            setReplyingToTrade={setReplyingToTrade}
            tradeReplyText={tradeReplyText}
            setTradeReplyText={setTradeReplyText}
            sendingTradeReply={sendingTradeReply}
            handleTradeReply={handleTradeReply}
            cancelTradeReply={cancelTradeReply}
          />
        )}

        {!studentDetail.sharing_enabled && !studentDetail.is_mentorship_student && (
          <div className="not-sharing-notice">
            <EyeOff size={20} />
            <p>This student has not enabled sharing. Performance data is not available.</p>
          </div>
        )}

        {/* Chat Section */}
        <div className="chat-section">
          <h4>
            <MessageSquare size={16} />
            Messages
          </h4>
          
          <div className="chat-messages">
            {(!studentDetail.messages || studentDetail.messages.length === 0) ? (
              <div className="no-messages">
                <p>No messages yet</p>
              </div>
            ) : (
              studentDetail.messages.map((msg, idx) => (
                <div 
                  key={msg.id || idx}
                  className={`message ${msg.sender === 'coach' ? 'sent' : 'received'}`}
                >
                  <div className="message-content">
                    <p>{msg.message}</p>
                    <div className="message-footer">
                      <span className="message-time">{formatTime(msg.timestamp)}</span>
                      <button 
                        className="message-reply-btn"
                        onClick={() => setReplyingToMessage(msg)}
                        title="Reply to this message"
                      >
                        <Reply size={14} />
                        <span>Reply</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply Preview */}
          {replyingToMessage && (
            <div className="reply-preview">
              <div className="reply-preview-content">
                <Reply size={14} />
                <div className="reply-preview-text">
                  <span className="reply-to-label">
                    Replying to {replyingToMessage.sender === 'coach' ? 'yourself' : selectedStudent?.name}
                  </span>
                  <span className="reply-original">
                    {replyingToMessage.message.length > 60 
                      ? replyingToMessage.message.substring(0, 60) + '...'
                      : replyingToMessage.message
                    }
                  </span>
                </div>
              </div>
              <button className="reply-preview-close" onClick={cancelMessageReply}>
                <X size={16} />
              </button>
            </div>
          )}

          <form className="chat-input" onSubmit={handleSendMessage}>
            <input
              type="text"
              placeholder={replyingToMessage ? "Type your reply..." : "Type a message..."}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={sending}
            />
            <button type="submit" disabled={!newMessage.trim() || sending}>
              {sending ? <Loader2 className="spin" size={18} /> : <Send size={18} />}
            </button>
          </form>
        </div>

        {/* Mentorship Sessions (if applicable) */}
        {studentDetail.is_mentorship_student && (
          <div className="sessions-section">
            <div className="sessions-header">
              <h4>
                <Award size={16} />
                Mentorship Sessions
              </h4>
              <button 
                className="btn-primary"
                onClick={() => {
                  setEditingSession(null);
                  setShowSessionModal(true);
                }}
              >
                <Plus size={16} />
                Schedule Session
              </button>
            </div>

            {sessions.length === 0 ? (
              <p className="no-sessions">No sessions scheduled yet</p>
            ) : (
              <div className="sessions-list">
                {/* Upcoming Sessions */}
                {sessions.filter(s => s.status === 'scheduled' || !s.status).length > 0 && (
                  <div className="sessions-section">
                    <h5 className="sessions-section-title upcoming">
                      <Calendar size={16} />
                      Upcoming Sessions
                    </h5>
                    {sessions
                      .filter(s => s.status === 'scheduled' || !s.status)
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .map((session) => (
                        <div key={session.id} className="session-card scheduled">
                          <div className="session-main">
                            <div className="session-datetime">
                              <Calendar size={16} />
                              <span className="session-date">
                                {new Date(session.date + 'T12:00:00').toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </span>
                              {session.time && (
                                <span className="session-time">at {session.time} ET</span>
                              )}
                            </div>
                            <div className="session-meta">
                              {session.series_id && (
                                <span className="session-frequency-tag">
                                  <RefreshCw size={12} />
                                  Series
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="session-links">
                            {session.zoom_link && (
                              <a 
                                href={session.zoom_link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="zoom-link"
                              >
                                <Play size={14} />
                                Join
                              </a>
                            )}
                          </div>
                          <div className="session-actions">
                            <button 
                              onClick={() => {
                                setEditingSession(session);
                                setShowSessionModal(true);
                              }}
                            >
                              <Edit3 size={14} />
                            </button>
                            <button 
                              onClick={async () => {
                                if (window.confirm('Delete this session?')) {
                                  await coachingApi.deleteSession(session.id);
                                  const data = await coachingApi.getStudentSessions(selectedStudent.id);
                                  setSessions(data.sessions || []);
                                }
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {/* Past Sessions */}
                {sessions.filter(s => s.status === 'completed').length > 0 && (
                  <div className="sessions-section">
                    <h5 className="sessions-section-title past">
                      <CheckCircle size={16} />
                      Past Sessions
                    </h5>
                    {sessions
                      .filter(s => s.status === 'completed')
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((session) => (
                        <div key={session.id} className="session-card completed">
                          <div className="session-header-row">
                            <div className="session-main">
                              <div className="session-datetime">
                                <Calendar size={16} />
                                <span className="session-date">
                                  {new Date(session.date + 'T12:00:00').toLocaleDateString('en-US', {
                                    month: 'long',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                            </div>
                            <div className="session-header-right">
                              {session.recording_url && (
                                <a 
                                  href={session.recording_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="recording-btn-small"
                                >
                                  <Play size={14} />
                                  Recording
                                </a>
                              )}
                              <button 
                                className="edit-btn"
                                onClick={() => {
                                  setEditingSession(session);
                                  setShowSessionModal(true);
                                }}
                              >
                                <Edit3 size={14} />
                              </button>
                              <button 
                                className="delete-btn"
                                onClick={async () => {
                                  if (window.confirm('Delete this session?')) {
                                    await coachingApi.deleteSession(session.id);
                                    const data = await coachingApi.getStudentSessions(selectedStudent.id);
                                    setSessions(data.sessions || []);
                                  }
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          
                          {/* Notes Section */}
                          {session.notes && (
                            <div className="session-notes-section">
                              <h6>Session Notes</h6>
                              <p>{session.notes}</p>
                            </div>
                          )}
                          
                          {/* Action Items */}
                          {session.action_items?.length > 0 && (
                            <div className="session-actions-section">
                              <h6>Action Items</h6>
                              <ul>
                                {session.action_items.map((item, idx) => (
                                  <li key={idx}>
                                    <CheckCircle size={14} />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}

                {/* Cancelled Sessions (collapsed) */}
                {sessions.filter(s => s.status === 'cancelled').length > 0 && (
                  <div className="sessions-section cancelled-section">
                    <h5 className="sessions-section-title cancelled">
                      <XCircle size={16} />
                      Cancelled ({sessions.filter(s => s.status === 'cancelled').length})
                    </h5>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="coaching-portal-overlay">
      <div className="coaching-portal">
        {/* Header */}
        <div className="portal-header">
          <div className="header-left">
            <button className="back-btn" onClick={onClose}>
              <ArrowLeft size={20} />
            </button>
            <h2>Coaching Portal</h2>
          </div>
          <button 
            className="settings-btn"
            onClick={() => {
              loadNotificationConfig();
              setShowNotificationSettings(true);
            }}
          >
            <Bell size={18} />
            Notifications
          </button>
        </div>

        {/* Tabs */}
        <div className="portal-tabs">
          <button 
            className={activeTab === 'students' ? 'active' : ''}
            onClick={() => {
              setActiveTab('students');
              setSelectedStudent(null);
              setStudentDetail(null);
            }}
          >
            <Users size={16} />
            All Students
          </button>
          <button 
            className={activeTab === 'mentorship' ? 'active' : ''}
            onClick={() => {
              setActiveTab('mentorship');
              setSelectedStudent(null);
              setStudentDetail(null);
            }}
          >
            <Star size={16} />
            Mentorship Students
          </button>
          <button
            className={activeTab === 'alerts' ? 'active' : ''}
            onClick={() => {
              setActiveTab('alerts');
              setSelectedStudent(null);
              setStudentDetail(null);
            }}
            data-testid="coaching-tab-alerts"
          >
            <AlertCircle size={16} />
            Alerts {alerts.length > 0 && <span style={{
              background: '#ef4444',
              color: '#fff',
              fontSize: 11,
              padding: '1px 7px',
              borderRadius: 10,
              marginLeft: 4,
              fontWeight: 700,
            }}>{alerts.length}</span>}
          </button>
        </div>

        {/* Main Content */}
        <div className="portal-content">
          {activeTab === 'alerts' ? (
            <div style={{ flex: 1, padding: 20, overflowY: 'auto' }} data-testid="alerts-panel">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <h2 style={{ margin: 0, color: '#fff', fontSize: 18 }}>Student Alerts</h2>
                  <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 13 }}>
                    Proactive heads-ups: streak milestones, broken runs, ghosting, and slumps.
                  </p>
                </div>
                <button
                  onClick={handleRunAlertScan}
                  disabled={runningScan}
                  data-testid="run-alert-scan-btn"
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: 'rgba(255,255,255,0.04)',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: runningScan ? 'wait' : 'pointer',
                    opacity: runningScan ? 0.6 : 1,
                  }}
                >
                  {runningScan ? 'Scanning…' : 'Run Scan Now'}
                </button>
              </div>

              {alerts.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  No active alerts. Your members are on track.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {alerts.map(a => {
                    const iconByType = {
                      big_milestone: Flame,
                      big_streak_broken: HeartCrack,
                      ghosting: Snowflake,
                      sustained_low: Ghost,
                    };
                    const colorByType = {
                      big_milestone: '#fbbf24',
                      big_streak_broken: '#ef4444',
                      ghosting: '#60a5fa',
                      sustained_low: '#a78bfa',
                    };
                    const Icon = iconByType[a.type] || AlertCircle;
                    const color = colorByType[a.type] || '#9ca3af';
                    return (
                      <div key={a.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        padding: '14px 18px',
                        borderRadius: 10,
                        background: 'rgba(15, 23, 42, 0.7)',
                        border: `1px solid ${color}44`,
                      }}>
                        <div style={{
                          flexShrink: 0, width: 36, height: 36, borderRadius: 8,
                          background: `${color}22`, color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Icon size={18} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{a.title}</div>
                          <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>{a.summary}</div>
                          <div style={{ color: '#6b7280', fontSize: 11, marginTop: 4 }}>
                            {a.student_email} · {new Date(a.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <button
                          onClick={() => handleResolveAlert(a.id)}
                          data-testid={`resolve-alert-${a.id}`}
                          title="Mark resolved"
                          style={{
                            flexShrink: 0,
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '6px 10px',
                            borderRadius: 6,
                            border: `1px solid ${color}66`,
                            background: 'rgba(255,255,255,0.04)',
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          <Check size={14} /> Resolve
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="students-panel">
                {renderStudentList()}
              </div>
              <div className="detail-panel">
                {renderStudentDetail()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Session Modal */}
      {showSessionModal && (
        <SessionModal
          session={editingSession}
          studentId={selectedStudent?.id}
          onClose={() => {
            setShowSessionModal(false);
            setEditingSession(null);
          }}
          onSave={async () => {
            const data = await coachingApi.getStudentSessions(selectedStudent.id);
            setSessions(data.sessions || []);
            setShowSessionModal(false);
            setEditingSession(null);
          }}
        />
      )}

      {/* Notification Settings Modal */}
      {showNotificationSettings && notificationConfig && (
        <div className="modal-overlay" onClick={() => setShowNotificationSettings(false)}>
          <div className="notification-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Admin Notification Settings</h3>
              <button onClick={() => setShowNotificationSettings(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Private Telegram Chat ID</label>
                <input
                  type="text"
                  value={notificationConfig.private_chat_id || ''}
                  onChange={(e) => setNotificationConfig({
                    ...notificationConfig,
                    private_chat_id: e.target.value
                  })}
                  placeholder="Your private chat ID"
                />
                <small>This is where you&apos;ll receive admin notifications</small>
              </div>

              <div className="notification-toggles">
                <h4>Notification Types</h4>
                {Object.entries(notificationConfig.notifications || {}).map(([key, value]) => (
                  <label key={key} className="toggle-item">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => setNotificationConfig({
                        ...notificationConfig,
                        notifications: {
                          ...notificationConfig.notifications,
                          [key]: e.target.checked
                        }
                      })}
                    />
                    <span>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleTestNotification}>
                Test Notification
              </button>
              <button className="btn-primary" onClick={handleSaveNotificationConfig}>
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Session Modal Component
const SessionModal = ({ session, studentId, onClose, onSave }) => {
  // Scheduling fields
  const [date, setDate] = useState(session?.date || new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState(session?.time || '10:00');
  const [frequency, setFrequency] = useState(session?.frequency || 'one-time');
  const [endAfterValue, setEndAfterValue] = useState(6);
  const [endAfterUnit, setEndAfterUnit] = useState('weeks');
  const [zoomLink, setZoomLink] = useState(session?.zoom_link || '');
  
  // Post-session fields
  const [recordingType, setRecordingType] = useState(session?.recording_type || 'link');
  const [recordingUrl, setRecordingUrl] = useState(session?.recording_url || '');
  const [notes, setNotes] = useState(session?.notes || '');
  const [actionItems, setActionItems] = useState(session?.action_items || []);
  const [newActionItem, setNewActionItem] = useState('');
  const [status, setStatus] = useState(session?.status || 'scheduled');
  const [updateAllFuture, setUpdateAllFuture] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeTab, setActiveTab] = useState(session ? 'details' : 'schedule');
  const fileInputRef = useRef(null);

  const isEditing = !!session;
  const isPartOfSeries = session?.series_id;

  const handleAddActionItem = () => {
    if (newActionItem.trim()) {
      setActionItems([...actionItems, newActionItem.trim()]);
      setNewActionItem('');
    }
  };

  const handleRemoveActionItem = (index) => {
    setActionItems(actionItems.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 3 * 1024 * 1024 * 1024) {
      alert('File too large. Maximum size is 3GB.');
      return;
    }

    try {
      const result = await coachingApi.uploadSessionVideo(
        session?.id,
        file,
        (progress) => setUploadProgress(progress)
      );
      setRecordingUrl(result.video_url);
      setRecordingType('upload');
      setUploadProgress(0);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload video');
      setUploadProgress(0);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      if (session) {
        // Editing existing session
        const sessionData = {
          date,
          time,
          zoom_link: zoomLink || null,
          status,
          recording_type: recordingType,
          recording_url: recordingUrl || null,
          notes,
          action_items: actionItems,
          update_all_future: updateAllFuture
        };
        await coachingApi.updateSession(session.id, sessionData);
      } else {
        // Creating new session(s)
        const sessionData = {
          date,
          time,
          frequency,
          zoom_link: zoomLink || null,
          // Only include end_after for recurring sessions
          ...(frequency !== 'one-time' && {
            end_after_value: endAfterValue,
            end_after_unit: endAfterUnit
          })
        };
        await coachingApi.createSession(studentId, sessionData);
      }
      
      onSave();
    } catch (error) {
      console.error('Failed to save session:', error);
      alert('Failed to save session');
    } finally {
      setSaving(false);
    }
  };

  const formatDateForDisplay = (dateStr, timeStr) => {
    const d = new Date(dateStr + 'T' + timeStr);
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    }) + ' at ' + d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) + ' ET';
  };

  // Calculate how many sessions will be created
  const calculateSessionCount = () => {
    if (frequency === 'one-time') return 1;
    const weeks = endAfterUnit === 'months' ? endAfterValue * 4 : endAfterValue;
    return Math.floor(weeks / (frequency === 'weekly' ? 1 : 2));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="session-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEditing ? 'Edit Session' : 'Schedule New Session'}</h3>
          <button onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs for editing */}
        {isEditing && (
          <div className="modal-tabs">
            <button 
              className={activeTab === 'schedule' ? 'active' : ''} 
              onClick={() => setActiveTab('schedule')}
            >
              <Calendar size={16} />
              Schedule
            </button>
            <button 
              className={activeTab === 'details' ? 'active' : ''} 
              onClick={() => setActiveTab('details')}
            >
              <FileText size={16} />
              Notes & Recording
            </button>
          </div>
        )}

        <div className="modal-body">
          {/* Schedule Tab / New Session Form */}
          {(activeTab === 'schedule' || !isEditing) && (
            <>
              <div className="schedule-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>{frequency !== 'one-time' && !isEditing ? 'Start Date' : 'Date'}</label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Time (ET)</label>
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                    />
                  </div>
                </div>

                {!isEditing && (
                  <div className="form-group">
                    <label>Frequency</label>
                    <div className="frequency-buttons">
                      <button 
                        type="button"
                        className={frequency === 'one-time' ? 'active' : ''}
                        onClick={() => setFrequency('one-time')}
                      >
                        One-time
                      </button>
                      <button 
                        type="button"
                        className={frequency === 'weekly' ? 'active' : ''}
                        onClick={() => setFrequency('weekly')}
                      >
                        Weekly
                      </button>
                      <button 
                        type="button"
                        className={frequency === 'biweekly' ? 'active' : ''}
                        onClick={() => setFrequency('biweekly')}
                      >
                        Bi-weekly
                      </button>
                    </div>
                  </div>
                )}

                {/* End Date - only for recurring sessions when creating new */}
                {!isEditing && frequency !== 'one-time' && (
                  <div className="form-group">
                    <label>Program Duration</label>
                    <div className="duration-selector">
                      <input
                        type="number"
                        min="1"
                        max="52"
                        value={endAfterValue}
                        onChange={(e) => setEndAfterValue(parseInt(e.target.value) || 1)}
                        className="duration-value"
                      />
                      <select
                        value={endAfterUnit}
                        onChange={(e) => setEndAfterUnit(e.target.value)}
                        className="duration-unit"
                      >
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                    <small className="duration-note">
                      This will create {calculateSessionCount()} sessions ({frequency === 'weekly' ? 'every week' : 'every 2 weeks'})
                    </small>
                  </div>
                )}

                <div className="form-group">
                  <label>Zoom Meeting Link</label>
                  <input
                    type="text"
                    placeholder="https://zoom.us/j/..."
                    value={zoomLink}
                    onChange={(e) => setZoomLink(e.target.value)}
                  />
                  <small>Student will see this link to join the session</small>
                </div>

                {isEditing && (
                  <div className="form-group">
                    <label>Status</label>
                    <div className="status-buttons">
                      <button 
                        type="button"
                        className={status === 'scheduled' ? 'active scheduled' : ''}
                        onClick={() => setStatus('scheduled')}
                      >
                        <Clock size={14} />
                        Scheduled
                      </button>
                      <button 
                        type="button"
                        className={status === 'completed' ? 'active completed' : ''}
                        onClick={() => setStatus('completed')}
                      >
                        <CheckCircle size={14} />
                        Completed
                      </button>
                      <button 
                        type="button"
                        className={status === 'cancelled' ? 'active cancelled' : ''}
                        onClick={() => setStatus('cancelled')}
                      >
                        <XCircle size={14} />
                        Cancelled
                      </button>
                    </div>
                  </div>
                )}

                {/* Update series option for recurring sessions */}
                {isEditing && isPartOfSeries && (
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={updateAllFuture}
                        onChange={(e) => setUpdateAllFuture(e.target.checked)}
                      />
                      Apply time/zoom changes to all future sessions in this series
                    </label>
                  </div>
                )}
              </div>

              {/* Preview */}
              <div className="session-preview">
                <div className="preview-header">
                  {isEditing ? 'Session Details' : `Creating ${calculateSessionCount()} Session${calculateSessionCount() > 1 ? 's' : ''}`}
                </div>
                <div className="preview-content">
                  <div className="preview-datetime">
                    <Calendar size={16} />
                    {formatDateForDisplay(date, time)}
                  </div>
                  {!isEditing && frequency !== 'one-time' && (
                    <div className="preview-frequency">
                      <RefreshCw size={14} />
                      Repeats {frequency} for {endAfterValue} {endAfterUnit}
                    </div>
                  )}
                  {zoomLink && (
                    <div className="preview-zoom">
                      <Video size={14} />
                      Zoom link added
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Details Tab (only for editing) */}
          {activeTab === 'details' && isEditing && (
            <>
              <div className="form-group">
                <label>Session Recording</label>
                <div className="recording-options">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="recordingType"
                      value="link"
                      checked={recordingType === 'link'}
                      onChange={() => setRecordingType('link')}
                    />
                    <Link size={16} />
                    Paste Link
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="recordingType"
                      value="upload"
                      checked={recordingType === 'upload'}
                      onChange={() => setRecordingType('upload')}
                    />
                    <Upload size={16} />
                    Upload Video
                  </label>
                </div>

                {recordingType === 'link' ? (
                  <input
                    type="text"
                    placeholder="https://zoom.us/rec/... or YouTube link"
                    value={recordingUrl}
                    onChange={(e) => setRecordingUrl(e.target.value)}
                  />
                ) : (
                  <div className="upload-area">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="video/*"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                    <button 
                      className="upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadProgress > 0}
                    >
                      {uploadProgress > 0 ? (
                        `Uploading... ${uploadProgress}%`
                      ) : recordingUrl ? (
                        'Change Video'
                      ) : (
                        'Choose Video'
                      )}
                    </button>
                    {recordingUrl && <span className="upload-success">Video uploaded ✓</span>}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  placeholder="Session notes, key takeaways, etc..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                />
              </div>

              <div className="form-group">
                <label>Action Items</label>
                <div className="action-items-input">
                  <input
                    type="text"
                    placeholder="Add action item..."
                    value={newActionItem}
                    onChange={(e) => setNewActionItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddActionItem())}
                  />
                  <button type="button" onClick={handleAddActionItem}>
                    <Plus size={16} />
                  </button>
                </div>
                {actionItems.length > 0 && (
                  <ul className="action-items-list">
                    {actionItems.map((item, idx) => (
                      <li key={idx}>
                        <CheckCircle size={14} />
                        <span>{item}</span>
                        <button onClick={() => handleRemoveActionItem(idx)}>
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="spin" size={16} /> : <Save size={16} />}
            {isEditing ? 'Save Changes' : 'Schedule Session'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Student Calendar Component
const StudentCalendar = ({ 
  calendarData, 
  calendarMonth, 
  calendarLoading, 
  selectedDay, 
  onMonthChange, 
  onSelectDay, 
  onRefresh,
  replyingToTrade,
  setReplyingToTrade,
  tradeReplyText,
  setTradeReplyText,
  sendingTradeReply,
  handleTradeReply,
  cancelTradeReply
}) => {
  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-').map(Number);
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const generateCalendarDays = () => {
    const [year, month] = calendarMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Empty cells for days before the first day of month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ empty: true, key: `empty-${i}` });
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = calendarData?.days?.find(d => d.date === dateStr);
      
      days.push({
        day,
        date: dateStr,
        data: dayData || null,
        key: dateStr
      });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();
  const dayDataMap = {};
  calendarData?.days?.forEach(d => { dayDataMap[d.date] = d; });

  return (
    <div className="student-calendar-section">
      <div className="calendar-header-row">
        <h4>
          <Calendar size={16} />
          Trading Calendar
        </h4>
        <div className="calendar-controls">
          <button className="refresh-calendar-btn" onClick={onRefresh} title="Refresh data">
            <RefreshCw size={16} className={calendarLoading ? 'spin' : ''} />
          </button>
          <div className="month-nav">
            <button onClick={() => onMonthChange('prev')}>
              <ChevronLeft size={18} />
            </button>
            <span className="month-label">{getMonthName(calendarMonth)}</span>
            <button onClick={() => onMonthChange('next')}>
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {calendarLoading ? (
        <div className="calendar-loading">
          <Loader2 className="spin" size={24} />
          <p>Loading calendar...</p>
        </div>
      ) : (
        <>
          <div className="calendar-grid">
            {/* Day headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="weekday">{day}</div>
            ))}
            
            {/* Calendar cells */}
            {calendarDays.map((item) => {
              if (item.empty) {
                return <div key={item.key} className="calendar-day empty"><span className="day-number">{item.day || ''}</span></div>;
              }
              
              const hasData = item.data !== null;
              const pl = item.data?.total_pl || 0;
              const tradeCount = item.data?.trade_count || 0;
              const habitScore = item.data?.habit_score;
              const totalPoints = item.data?.total_points;
              const hasPoints = item.data?.has_points;
              const isSelected = selectedDay?.date === item.date;
              
              return (
                <div
                  key={item.key}
                  className={`calendar-day ${hasData ? 'has-data' : ''} ${isSelected ? 'selected' : ''} ${pl > 0 ? 'positive' : pl < 0 ? 'negative' : ''}`}
                  onClick={() => hasData && onSelectDay(item.data)}
                >
                  <span className="day-number">{item.day}</span>
                  {hasData && (
                    <div className="day-info">
                      {tradeCount > 0 && (
                        <>
                          <span className={`day-pl ${pl >= 0 ? 'positive' : 'negative'}`}>
                            {pl >= 0 ? '' : '-'}${Math.abs(pl).toFixed(2)}
                          </span>
                          <span className="day-trade-count">{tradeCount} trade{tradeCount !== 1 ? 's' : ''}</span>
                          {hasPoints && totalPoints !== null && (
                            <span className={`day-points ${totalPoints >= 0 ? 'positive' : 'negative'}`}>
                              {totalPoints >= 0 ? '+' : ''}{Number.isInteger(totalPoints) ? totalPoints : totalPoints.toFixed(2)}
                            </span>
                          )}
                        </>
                      )}
                      {habitScore !== null && (
                        <span className="day-habit">{habitScore}%</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selected Day Details */}
          {selectedDay && (
            <div className="selected-day-details">
              <div className="day-detail-header">
                <h5>
                  {new Date(selectedDay.date + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h5>
                <button className="close-day" onClick={() => onSelectDay(null)}>
                  <X size={16} />
                </button>
              </div>

              <div className="day-summary">
                <div className="summary-item">
                  <span className="label">P/L</span>
                  <span className={`value ${selectedDay.total_pl >= 0 ? 'positive' : 'negative'}`}>
                    {selectedDay.total_pl >= 0 ? '+' : ''}${selectedDay.total_pl}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="label">Trades</span>
                  <span className="value">{selectedDay.wins}W / {selectedDay.losses}L</span>
                </div>
                {selectedDay.has_points && selectedDay.total_points !== null && (
                  <div className="summary-item">
                    <span className="label">Points</span>
                    <span className={`value ${selectedDay.total_points >= 0 ? 'positive' : 'negative'}`}>
                      {selectedDay.total_points >= 0 ? '+' : ''}{Number.isInteger(selectedDay.total_points) ? selectedDay.total_points : selectedDay.total_points.toFixed(2)}
                    </span>
                  </div>
                )}
                {selectedDay.habit_score !== null && (
                  <div className="summary-item">
                    <span className="label">Habit Score</span>
                    <span className="value">{selectedDay.habit_score}%</span>
                  </div>
                )}
              </div>

              {/* Trades */}
              {selectedDay.trades?.length > 0 && (
                <div className="day-trades">
                  <h6>Trades</h6>
                  <div className="trades-list">
                    {selectedDay.trades.map((trade, idx) => {
                      const tradeKey = trade.id || idx;
                      const hasNote = trade.notes && trade.notes.trim();
                      const isReplying = replyingToTrade === tradeKey;
                      
                      return (
                        <div key={tradeKey} className="trade-item">
                          <div className="trade-main">
                            <span className="trade-pair">{trade.currency_pair || trade.pair}</span>
                            <span className={`trade-direction ${trade.direction?.toLowerCase()}`}>
                              {trade.direction}
                            </span>
                            <span className={`trade-pl ${trade.outcome === 'Win' ? 'positive' : 'negative'}`}>
                              {trade.outcome === 'Win' ? '+' : '-'}${trade.profit_loss}
                            </span>
                          </div>
                          <div className="trade-details">
                            {trade.session && <span className="trade-session">{trade.session}</span>}
                            {trade.time && <span className="trade-time">{trade.time}</span>}
                          </div>
                          <div className="trade-prices">
                            {(trade.entry || trade.entry_price) && (
                              <span className="trade-entry">Entry: {trade.entry || trade.entry_price}</span>
                            )}
                            {trade.stop_loss && (
                              <span className="trade-sl">SL: {trade.stop_loss}</span>
                            )}
                            {trade.take_profit && (
                              <span className="trade-tp">TP: {trade.take_profit}</span>
                            )}
                          </div>
                          {(trade.lot_size || trade.pips_points) && (
                            <div className="trade-metrics">
                              {trade.lot_size && <span>Lot Size: {trade.lot_size}</span>}
                              {trade.pips_points && <span>Pips: {trade.pips_points}</span>}
                            </div>
                          )}
                          {trade.notes && (
                            <div className="trade-notes">
                              <p>{trade.notes}</p>
                            </div>
                          )}
                          
                          {/* Reply/Comment Button */}
                          {!isReplying && (
                            <button 
                              className={`trade-reply-btn ${hasNote ? 'reply' : 'comment'}`}
                              onClick={() => setReplyingToTrade(tradeKey)}
                            >
                              <Reply size={14} />
                              {hasNote ? 'Reply' : 'Comment'}
                            </button>
                          )}
                          
                          {/* Reply Input Area */}
                          {isReplying && (
                            <div className="trade-reply-form">
                              <textarea
                                value={tradeReplyText}
                                onChange={(e) => setTradeReplyText(e.target.value)}
                                placeholder={hasNote ? "Reply to this note..." : "Add a coaching comment..."}
                                rows={3}
                                autoFocus
                              />
                              <div className="trade-reply-actions">
                                <button 
                                  className="cancel-btn"
                                  onClick={cancelTradeReply}
                                  disabled={sendingTradeReply}
                                >
                                  Cancel
                                </button>
                                <button 
                                  className="send-btn"
                                  onClick={() => handleTradeReply(trade)}
                                  disabled={!tradeReplyText.trim() || sendingTradeReply}
                                >
                                  {sendingTradeReply ? (
                                    <Loader2 size={14} className="spin" />
                                  ) : (
                                    <Send size={14} />
                                  )}
                                  Send
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Habit Rules */}
              {selectedDay.rules_followed?.length > 0 && (
                <div className="day-habits">
                  <h6>Trading Rules</h6>
                  <div className="rules-list">
                    {selectedDay.rules_followed.map((rule, idx) => (
                      <div key={idx} className={`rule-item ${rule.followed ? 'followed' : 'missed'}`}>
                        {rule.followed ? (
                          <CheckCircle size={14} className="check" />
                        ) : (
                          <XCircle size={14} className="x" />
                        )}
                        <span className="rule-text">{rule.rule_text || `Rule ${idx + 1}`}</span>
                        {rule.notes && <span className="rule-notes">{rule.notes}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CoachingPortal;
